import {Component, inject, OnInit} from '@angular/core';
import {SurveyService} from '../../services/survey.service';
import {combineLatest, first} from 'rxjs';
import {Survey} from '../../models/survey.models';
import {SurveyComponent} from '../../components/survey/survey.component';
import {LogService} from '../../services/log.service';
import {GoogleAnalyticsService} from "../../services/google-analytics.service";
import ErrorModalComponent, {ErrorModalData} from "../../components/error-modal/error-modal.component";
import {ModalService} from "../../services/modal.service";
import {NavigationService} from '../../services/navigation.service';
import LoaderComponent from "../../components/loader/loader.component";
import {NgIf} from "@angular/common";
import {InfoModalComponent, InfoModalData} from '../../components/info-modal/info-modal.component';

@Component({
    selector: 'survey-page',
    standalone: true,
    imports: [SurveyComponent, LoaderComponent, NgIf],
    templateUrl: './survey-page.component.html',
    styleUrl: './survey-page.component.css',
})
export default class SurveyPageComponent implements OnInit {
    #surveyService = inject(SurveyService);
    #loggerService = inject(LogService);
    #googleAnalyticsService = inject(GoogleAnalyticsService);
    #modalService = inject(ModalService);
    #navigationService = inject(NavigationService);

    initialSurvey?: Survey;
    isSendingSurvey = false;
    hasSummaryPage = true;

    ngOnInit() {
        combineLatest([
            this.#surveyService.checkInitialSurveyValidity(),
            this.#surveyService.getInitialSurvey(),
        ]).pipe(first()).subscribe({
            next: ([isValid, survey]) => {
                if (isValid) {
                    this.#navigationService.navigateBack();
                    return;
                }
                this.initialSurvey = survey;
            },
            error: (err) => {
                this.#loggerService.error(err);
                this.#modalService.open<ErrorModalData, ErrorModalComponent>(
                    ErrorModalComponent,
                    {
                        data: {
                            error: err,
                            message: 'Non è stato possibile caricare il questionario',
                            onCloseCallback: () => {
                                this.#navigationService.navigateBack();
                            }
                        },
                    }
                );
            }
        });
    }

    onSurveyNextPage(event: { currentPage: number, prevPage: number }) {
        const page = this.initialSurvey?.surveyVersion.surveyPages[event.prevPage];
        const question = page?.type === 'Questions' ? page.surveyQuestions?.[0].questionName : page?.description;
        this.#googleAnalyticsService.sendAnalyticsEvent({
            survey: 'funnel_survey',
            eventName: `step${event.prevPage + 1}`,
            eventParams: 'avanti',
            eventParamsSecond: this.removeHtmlTags(question ?? '')
        });
    }

    onSurveyPrevPage(event: { currentPage: number, prevPage: number }) {
        const pages = this.initialSurvey?.surveyVersion.surveyPages;
        const page = pages?.[event.prevPage];
        const question = page?.type === 'Questions' ? page.surveyQuestions?.[0].questionName : page?.description;
        this.#googleAnalyticsService.sendAnalyticsEvent({
            survey: 'funnel_survey',
            eventName: `step${event.prevPage + 1}`,
            eventParams: 'indietro',
            eventParamsSecond: this.hasSummaryPage && event.prevPage === pages?.length ?
                'Rivediamo le tue scelte' : this.removeHtmlTags(question ?? '')
        });
    }

    onCancelSurvey() {
        const page = this.initialSurvey?.surveyVersion.surveyPages[0];
        const question = page?.type === 'Questions' ? page.surveyQuestions?.[0].questionName : page?.description;
        this.#googleAnalyticsService.sendAnalyticsEvent({
            survey: 'funnel_survey',
            eventName: 'step1',
            eventParams: 'indietro',
            eventParamsSecond: this.removeHtmlTags(question ?? '')
        });
    }

    onSurveySubmit(survey: Survey) {
        if (this.isSendingSurvey) {
            return;
        }
        this.isSendingSurvey = true;

        this.#loggerService.log(survey);

        const lastPage = survey.surveyVersion.surveyPages[survey.surveyVersion.surveyPages.length - 1];
        const question = lastPage?.type === 'Questions' ? lastPage.surveyQuestions?.[0].questionName : lastPage?.description;
        this.#googleAnalyticsService.sendAnalyticsEvent({
            survey: 'funnel_survey',
            eventName: `step${survey.surveyVersion.surveyPages.length + (this.hasSummaryPage ? 1 : 0)}`,
            eventParams: 'conferma',
            eventParamsSecond: this.hasSummaryPage ? 'Rivediamo le tue scelte' : this.removeHtmlTags(question ?? '')
        });

        this.#surveyService.postInitialSurvey(survey).subscribe({
            next: value => {
                this.#loggerService.log(value);
                this.isSendingSurvey = false;
                this.#modalService.open<InfoModalData, InfoModalComponent>(
                    InfoModalComponent,
                    {
                        data: {
                            title: 'Questionario inviato',
                            message: 'Ottimo, abbiamo ricevuto le tue risposte!' + (value != null ? ' Un coach è già a tua disposizione.' : ''),
                            buttonLabel: 'Torna indietro',
                            onCloseCallback: () => {
                                this.#navigationService.navigateBack(false);
                            }
                        },
                    }
                );
            },
            error: error => {
                this.#loggerService.error(error);
                this.isSendingSurvey = false;
                this.#modalService.open<ErrorModalData, ErrorModalComponent>(
                    ErrorModalComponent,
                    {
                        data: {
                            error: error,
                            message: 'Non è stato possibile inviare il questionario.',
                        },
                    }
                );
            }
        });
    }

    private removeHtmlTags(str: string): string {
        const doc = new DOMParser().parseFromString(str, 'text/html');
        return doc.body.textContent ?? '';
    }
}
